/**
 * Tiny pub-sub for the global error toast.
 *
 * Wired up from QueryCache.onError (App.tsx) — every failed useQuery
 * surfaces here so the user sees a red toast instead of a silent
 * empty table. Uses useSyncExternalStore so React 18 batching doesn't
 * skip transient errors.
 *
 * Single-slot: a fresh error replaces the previous one. If 10 queries
 * fail in parallel, only the last one is shown — better than flooding
 * the screen, and the user can re-trigger to see if any persist.
 */

import { useSyncExternalStore } from "react";

const listeners = new Set<() => void>();
let current: { message: string; id: number } | null = null;
let counter = 0;

export function setGlobalError(message: string): void {
  counter += 1;
  current = { message, id: counter };
  listeners.forEach((l) => l());
}

export function clearGlobalError(id: number): void {
  if (current?.id === id) {
    current = null;
    listeners.forEach((l) => l());
  }
}

export function useGlobalError(): { message: string; id: number } | null {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
    () => current,
    () => null,
  );
}
